import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { instagramUrl } from "@/mocks/site";

const NEWSLETTER_ENDPOINT = "https://readdy.ai/api/form/davpa6j2asjjtt1sku1g";

export default function SiteFooter() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("company_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") params.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(NEWSLETTER_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "Something went wrong. Please try again.";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg));
      }
    } catch {
      setStatus("error");
      setFormError("Network error. Please try again.");
    }
  }

  return (
    <footer className="bg-background-100 border-t border-background-200">
      <div className="px-4 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div className="max-w-md">
            <p className="font-heading text-lg text-foreground-950 mb-1">
              Newsletter Block
            </p>
            <p className="text-sm text-foreground-600 mb-5">
              Subscribe to our newsletter to see new project releases and DF updates.
            </p>

            <form
              id="newsletter-form-df"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3"
            >
              <input
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder="Email Address"
                className="w-full sm:flex-1 rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-aux-field"
              />
              <button
                type="submit"
                disabled={status === "submitting"}
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-xs tracking-[0.18em] uppercase text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
              >
                {status === "submitting" ? "Submitting" : "Subscribe"}
              </button>
            </form>

            <div className="mt-3 min-h-[20px] text-sm">
              {status === "success" && (
                <span className="text-accent-700">Thank you! You're on the list.</span>
              )}
              {status === "error" && <span className="text-accent-800">{formError}</span>}
              {status === "idle" && (
                <span className="text-xs text-foreground-500">We respect your privacy.</span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-6 lg:items-end lg:text-right">
            <nav className="flex flex-col gap-3">
              <Link
                to="/contact"
                className="font-heading text-lg text-foreground-900 hover:text-accent-700 transition-colors"
              >
                CAREERS
              </Link>
              <Link
                to="/work"
                className="font-heading text-lg text-foreground-900 hover:text-accent-700 transition-colors"
              >
                BLOG
              </Link>
            </nav>
            <a
              href={instagramUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="w-10 h-10 flex items-center justify-center text-foreground-800 hover:text-accent-700 transition-colors"
            >
              <i className="ri-instagram-line text-2xl" />
            </a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-200 flex flex-col sm:flex-row gap-6 sm:gap-10 sm:justify-between">
          <p className="text-xs leading-relaxed text-foreground-500 max-w-lg">
            All artwork belongs to Definition Films and/or respective clientele. © 2026
            Definition Films. All rights reserved.
            <br />
            DEFINITION FILMS is a registered trademark name for Definition Films, LLC.
          </p>
          <div className="sm:text-right">
            <p className="font-heading text-sm tracking-wide text-foreground-900">
              DEFINITION FILMS ®
            </p>
            <p className="text-xs text-foreground-500 mt-1">Based in Sonoma County, CA</p>
          </div>
        </div>
      </div>
    </footer>
  );
}